/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import {
  OUTCOME_LABEL,
  RECORD_OUTCOMES,
} from '@sportninja/common/features/competition/operations/utils/recordBarSegments'
import { font, paragraphSmall, weightMedium } from 'src/components/css'
import { RECORD_COLORS } from './recordColors'

/**
 * Key for the record bar's five outcome colours.
 *
 * Shared by the Division Performance chart and the teams sheet so the two
 * cannot drift apart — it was previously private to `DivisionStandings`.
 * Colour is never the only channel (every row also prints its record as text),
 * but the legend is the dependable identity channel and is always present.
 */
const RecordLegend = () => {
  'use memo'

  return (
    <div css={legendStyle}>
      {RECORD_OUTCOMES.map((outcome) => (
        <span key={outcome} css={legendItemStyle}>
          <i css={swatchStyle(RECORD_COLORS[outcome])} />
          {OUTCOME_LABEL[outcome]}
        </span>
      ))}
    </div>
  )
}

/* One row, always. The full-word labels are too wide to wrap gracefully, so on
   a narrow panel the legend scrolls sideways inside its own box rather than
   stacking or squeezing whatever sits beside it. */
const legendStyle = css`
  display: flex;
  align-items: center;
  gap: 13px;
  flex-wrap: nowrap;
  overflow-x: auto;
  min-width: 0;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`

const legendItemStyle = css`
  ${font.body}
  ${paragraphSmall}
  ${weightMedium}
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${colors.NEUTRAL_50};
  /* Without this, flexbox shrinks the items and breaks the words mid-label. */
  flex: none;
  white-space: nowrap;
`

const swatchStyle = (color: string) => css`
  width: 9px;
  height: 9px;
  border-radius: 2px;
  background: ${color};
  flex: none;
`

export default RecordLegend
